import { useState } from 'react';
import { PageHeader, Button, Card, Badge } from 'components/ui';
import { LoadingState, ErrorState, EmptyState } from 'components/feedback';
import { CreditCardIcon, PlusIcon, CheckCircleIcon, EditIcon, StoreIcon } from 'components/icons';
import { useToast } from 'components/toast/ToastProvider';
import { humanizeEnum } from 'lib/formatters';
import { useShopName } from 'components/ShopSelect';
import { useBankAccounts, useBankAccountMutations } from '../hooks/useBankAccounts';
import { BankAccountModal } from '../components/BankAccountModal';

const STATUS_TONES = {
  ACTIVE: 'success',
  VERIFIED: 'success',
  COMPLETED: 'success',
  PENDING: 'warning',
  CREATED: 'info',
  FAILED: 'danger',
  REJECTED: 'danger',
};

const statusTone = (status) => STATUS_TONES[status] || 'neutral';
const last4 = (value) => (value ? String(value).slice(-4) : '••••');

/** Lists linked bank accounts with add / edit / set-primary / verify. */
export function BankAccountsPage() {
  const { accounts, loading, error, refetch } = useBankAccounts();
  const {
    createAccount,
    updateAccount,
    setPrimaryAccount,
    verifyAccount,
    creating,
    updating,
    settingPrimary,
    verifying,
  } = useBankAccountMutations();
  const toast = useToast();
  const getShopName = useShopName();

  const [modal, setModal] = useState({ open: false, account: null });
  const [busyId, setBusyId] = useState(null);

  const handleSetPrimary = async (account) => {
    setBusyId(account.id);
    try {
      const result = await setPrimaryAccount(account.id);
      toast.success('Primary account updated', result?.message || undefined);
    } catch (err) {
      toast.error('Could not update', err.message);
    } finally {
      setBusyId(null);
    }
  };

  const handleVerify = async (account) => {
    setBusyId(account.id);
    try {
      const result = await verifyAccount(account.razorpayAccountId);
      if (result?.error) {
        toast.error('Verification failed', result.error);
      } else {
        toast.success(
          'Verification requested',
          result?.linkedAccountStatus ? `Status: ${humanizeEnum(result.linkedAccountStatus)}` : undefined,
        );
      }
    } catch (err) {
      toast.error('Something went wrong', err.message);
    } finally {
      setBusyId(null);
    }
  };

  let content;
  if (loading && accounts.length === 0) {
    content = <LoadingState label="Loading bank accounts…" />;
  } else if (error && accounts.length === 0) {
    content = <ErrorState error={error} onRetry={() => refetch()} />;
  } else if (accounts.length === 0) {
    content = (
      <EmptyState
        icon={CreditCardIcon}
        title="No bank accounts"
        description="Add a bank account to receive your payouts."
        action={
          <Button
            leftIcon={<PlusIcon className="h-4 w-4" />}
            onClick={() => setModal({ open: true, account: null })}
          >
            Add account
          </Button>
        }
      />
    );
  } else {
    content = (
      <div className="space-y-3">
        {accounts.map((account) => (
          <Card key={account.id}>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-900">{account.beneficiaryName}</p>
                <p className="text-xs text-slate-500">
                  •••• {last4(account.accountNumber)} · {account.ifscCode}
                </p>
                {getShopName(account.vendorId) && (
                  <p className="mt-1 flex items-center gap-1 text-xs text-slate-400">
                    <StoreIcon className="h-3.5 w-3.5 shrink-0" />
                    <span className="truncate">{getShopName(account.vendorId)}</span>
                  </p>
                )}
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {account.isPrimaryAccount && <Badge tone="brand">Primary</Badge>}
                  {account.status && (
                    <Badge tone={statusTone(account.status)}>{humanizeEnum(account.status)}</Badge>
                  )}
                  {account.kycStatus && (
                    <Badge tone="neutral">KYC: {humanizeEnum(account.kycStatus)}</Badge>
                  )}
                </div>
              </div>

              <div className="flex shrink-0 flex-wrap items-center gap-2">
                {!account.isPrimaryAccount && (
                  <Button
                    size="sm"
                    variant="secondary"
                    isLoading={settingPrimary && busyId === account.id}
                    onClick={() => handleSetPrimary(account)}
                  >
                    Set primary
                  </Button>
                )}
                {account.razorpayAccountId && (
                  <Button
                    size="sm"
                    variant="ghost"
                    leftIcon={<CheckCircleIcon className="h-4 w-4" />}
                    isLoading={verifying && busyId === account.id}
                    onClick={() => handleVerify(account)}
                  >
                    Verify
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="ghost"
                  aria-label="Edit account"
                  leftIcon={<EditIcon className="h-4 w-4" />}
                  onClick={() => setModal({ open: true, account })}
                />
              </div>
            </div>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl">
      <PageHeader
        title="Bank accounts"
        description="Where your payouts are deposited."
        actions={
          accounts.length > 0 && (
            <Button
              leftIcon={<PlusIcon className="h-4 w-4" />}
              onClick={() => setModal({ open: true, account: null })}
            >
              Add account
            </Button>
          )
        }
      />
      {content}

      <BankAccountModal
        key={modal.account?.id || 'new'}
        isOpen={modal.open}
        account={modal.account}
        onClose={() => setModal({ open: false, account: null })}
        createAccount={createAccount}
        updateAccount={updateAccount}
        saving={creating || updating}
      />
    </div>
  );
}

export default BankAccountsPage;
